import React, { memo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import Dropzone from 'react-dropzone';

import TextSnippetWrapper from 'components/TextSnippetWrapper';
import LabelName from 'components/UI/LabelName/LabelName';

import { sanitizeHtmlColorContent } from 'utils/utils';
import { convertToHtml } from 'utils/utility_functions/convertToHtml';
import { ACCEPTED_FILE_TYPES } from '../../constants';
import { useCompanionCaseMessage } from '../../hooks/useCompanionCaseMessage';

import './CompanionCaseMessageModal.scss';

// ── Helpers ───────────────────────────────────────────────────────────────────

function hasTextContent(html) {
  if (!html) return false;
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim().length > 0;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const CaseContactList = memo(function CaseContactList({ caseItem, selections, onSelect }) {
  return (
    <div className="case-section card-bg">
      <div className="case-name-head">
        <div className="case-name">{caseItem.case_name}</div>
      </div>
      <div className="contacts-list">
        {caseItem.contacts.map((contact) => (
          <div key={contact.contact_id} className={`contact-item ${contact.is_primary_contact ? 'primary-contact' : ''}`}>
            <input
              type="checkbox"
              id={`contact-${caseItem.case_id}-${contact.contact_id}`}
              checked={selections[caseItem.case_id]?.[contact.contact_id] || false}
              onChange={(e) => onSelect(caseItem.case_id, contact.contact_id, e.target.checked)}
              className="contact-checkbox"
            />
            <label htmlFor={`contact-${caseItem.case_id}-${contact.contact_id}`} className="contact-label">
              <span className="contact-name">{contact.full_name}</span>
              {contact.is_primary_contact && (
                <span className="blue-tag">
                  <span className="blue-tag-value text-nowrap">Primary</span>
                </span>
              )}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

function CompanionCaseMessageModal({ show, setShow, caseDetails }) {
  const mainCaseErrorRef = useRef(null);
  const [showMainCaseError, setShowMainCaseError] = useState(false);

  const {
    allCases,
    selections,
    totalSelected,
    hasMainCaseSelected,
    handleSelect,
    handleQuickSelectPrimary,
    files,
    uploadProgress,
    isUploading,
    removeFile,
    handleFileDrop,
    sendMessages,
    isSending,
    reset: resetHook,
  } = useCompanionCaseMessage({ caseDetails, show });

  const {
    control,
    handleSubmit,
    watch,
    reset: resetForm,
    formState: { errors, isDirty },
  } = useForm({ defaultValues: { subject: '', message: '' } });

  const subject = watch('subject');
  const message = watch('message');

  const isBusy = isSending || isUploading;

  const close = () => {
    resetForm();
    resetHook();
    setShow(false);
    setShowMainCaseError(false);
  };

  const confirmClose = () => {
    const hasData = totalSelected > 0 || subject?.trim() || hasTextContent(message) || files.length > 0 || isDirty;

    if (hasData) {
      confirmDialog({
        message: 'Are you sure you want to close? All unsaved changes will be lost.',
        header: 'Confirm Close',
        icon: 'pi pi-exclamation-triangle',
        accept: close,
      });
    } else {
      close();
    }
  };

  const onSubmit = async (formData) => {
    if (!hasMainCaseSelected) {
      setShowMainCaseError(true);
      setTimeout(() => mainCaseErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      return;
    }
    setShowMainCaseError(false);
    await sendMessages(formData, { onSuccess: close });
  };

  if (!show) return null;

  return (
    <Dialog
      header={() => <i className="icon-window-filled modal-close-icon F-size14 pointer" onClick={confirmClose} />}
      visible={show}
      onHide={confirmClose}
      className="create-msg-popup"
      style={{ width: '90vw', maxWidth: '800px', minWidth: '600px' }}
      closable={false}
      resizable={false}
      draggable={false}
      modal
      blockScroll
    >
      <div className="companion-case-message-content">
        <h5 className="modal-title">Send Message to Companion Cases</h5>

        <div className="description-box d-flex align-items-center">
          <i className="pi pi-info-circle me-2" />
          <p className="F-size12 mb-0">
            Send the same message to users across all companion cases. Each message is sent individually — this is not a group message.
          </p>
        </div>

        {/* Recipients */}
        <div className="select-recipients-section mt-3">
          <div className="section-header">
            <div className="section-title-with-error">
              <LabelName required className="section-title">
                Select Recipients
              </LabelName>
              {showMainCaseError && !hasMainCaseSelected && (
                <div className="text-danger mt-2" ref={mainCaseErrorRef}>
                  Please select at least one contact from the current case
                </div>
              )}
            </div>
            <Button
              label="Quick Select Primary Contacts"
              icon="pi pi-check"
              className="p-button p-button-secondary"
              onClick={handleQuickSelectPrimary}
            />
          </div>

          <div className="selected-count">{totalSelected} contacts selected</div>

          {allCases.map((caseItem) => (
            <CaseContactList key={caseItem.case_id} caseItem={caseItem} selections={selections} onSelect={handleSelect} />
          ))}
        </div>

        {/* Subject */}
        <div className="subject-section mt-3">
          <LabelName required className="section-title">
            Subject:
          </LabelName>
          <Controller
            name="subject"
            control={control}
            rules={{ required: 'Please enter a subject' }}
            render={({ field }) => (
              <TextSnippetWrapper
                type="text"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="Subject"
                className="w-100 input-shadow"
              />
            )}
          />
          {errors.subject && <small className="text-danger">{errors.subject.message}</small>}
        </div>

        {/* Message */}
        <div className="mt-3">
          <LabelName required className="section-title">
            Message:
          </LabelName>
          <div className="message-attachments-section">
            <div className="message-section">
              <Controller
                name="message"
                control={control}
                rules={{ validate: (v) => hasTextContent(v) || 'Please enter a message' }}
                render={({ field }) => (
                  <TextSnippetWrapper
                    type="editor"
                    value={sanitizeHtmlColorContent(field.value)}
                    style={{ height: '150px' }}
                    onChange={(e) => field.onChange(convertToHtml(e))}
                  />
                )}
              />
              {errors.message && <small className="text-danger">{errors.message.message}</small>}
            </div>

            <div className="attachments-section">
              <Dropzone onDrop={handleFileDrop} accept={ACCEPTED_FILE_TYPES}>
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()} className="attachment-dropzone">
                    <input {...getInputProps()} />
                    <div className="attachment-area">
                      <i className="pi pi-paperclip attachment-icon" />
                      <span className="attachment-text">Click to attach files (optional)</span>
                    </div>
                  </div>
                )}
              </Dropzone>

              {files.length > 0 && (
                <div className="file-list">
                  {files.map((file, i) => (
                    <div key={file.path || i} className="file-item">
                      <span className="file-name">{file.name}</span>
                      <i className="icon-delete icon-red pointer" onClick={() => removeFile(file.path)} />
                    </div>
                  ))}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                      </div>
                      <span className="progress-text">{uploadProgress}% uploaded</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer-btn justify-content-end d-flex mt-3">
          <Button label="Cancel" className="p-button p-button-secondary outline me-2" onClick={confirmClose} disabled={isBusy} />
          <Button
            label={isBusy ? 'Sending...' : 'Send Messages'}
            className="p-button p-button-secondary"
            onClick={handleSubmit(onSubmit)}
            disabled={isBusy || totalSelected === 0}
            loading={isBusy}
          />
        </div>
      </div>
    </Dialog>
  );
}

export default memo(CompanionCaseMessageModal);
